/**
 * Aprovações — a porta de entrada do material no estoque.
 *
 * Alguém fotografa a nota (ou o material que chegou sem nota), a IA lê, e aqui
 * um responsável confere e decide. **Aprovar é o que de fato cria as entradas**
 * — nada entra no estoque sem passar por esta tela.
 *
 * A DECISÃO DE DESENHO QUE MANDA NO RESTO: a leitura da IA é PALPITE, e a tela
 * inteira trata assim. Ela aparece como sugestão ao lado da foto, nunca como
 * dado já aceito, e nenhum item entra no estoque sem alguém ter escolhido o
 * código a dedo. É tentador deixar o sistema casar sozinho a descrição da nota
 * com o item cadastrado — mas material errado no estoque é caro e demora a
 * aparecer: só descobre no inventário, meses depois, sem ninguém saber de onde
 * veio. A conferência é o produto aqui, não o atrito.
 *
 * Por isso a foto fica GRANDE e do lado do formulário: quem confere lê a nota
 * na tela, não de memória.
 */

import { api, ErroApi, type ItemDaLista } from '../api.js';
import { escapar, dataHora, quando, numero } from '../comum/formato.js';
import { avisar, avisarBloqueante } from '../comum/aviso.js';

type StatusAprovacao = 'pendente' | 'aprovado' | 'rejeitado';

/**
 * O palpite é `jsonb` de propósito no banco: o formato muda quando o prompt da
 * IA muda, e não queremos migração de banco a cada ajuste. O preço é aqui — a
 * tela precisa aceitar que qualquer campo venha faltando.
 */
interface PalpiteNf {
  numero_nf?: string;
  fornecedor?: string;
  data_emissao?: string;
  itens?: {
    descricao?: string;
    quantidade?: number;
    unidade?: string;
    valor_unitario?: number;
    lote?: string;
    validade?: string;
  }[];
}

interface PalpiteMaterial {
  palpite_nome?: string;
  marca?: string;
  quantidade_estimada?: number;
  unidade_provavel?: string;
  categoria_provavel?: string;
  codigo_barras?: string;
  observacoes?: string;
}

interface Aprovacao {
  id: number;
  enviada_em: string;
  operador_nome: string | null;
  tipo: string | null;
  foto_url: string | null;
  /** A mesma foto reduzida, para a miniatura da fila não baixar a grande. */
  foto_min: string | null;
  /** Data URI embutido — chega junto com o JSON, sem requisição de imagem. */
  miniatura: string | null;
  palpite: (PalpiteNf & PalpiteMaterial & { tipo?: string }) | null;
  obs: string | null;
  status: StatusAprovacao;
  motivo_recusa: string | null;
  decidida_em: string | null;
  decidida_por_nome: string | null;
  itensLancados: number;
}

/** Uma linha do formulário: o que a IA leu, e o que a pessoa decidiu. */
interface Linha {
  /** O texto lido da nota. Fica visível o tempo todo, como referência. */
  lido: string;
  /** O item do estoque escolhido. Sem ele, não dá para aprovar. */
  escolhido?: ItemDaLista;
  quantidade: number;
  lote: string;
  validade: string;
  marca: string;
  /**
   * A foto da identificação virando foto do ITEM (só material sem NF — em
   * nota fiscal a foto é a nota, não o produto). `segunda` é um upload extra;
   * `capa` diz qual das duas abre a galeria — dá para trocar antes de aprovar.
   */
  fotoItem?: {
    usar: boolean;
    segunda?: { caminho: string; url: string };
    capa: 'aprovacao' | 'segunda';
  };
}

export function montarAprovacoes(raiz: HTMLElement): void {
  let filtro: StatusAprovacao = 'pendente';
  let aberta: number | null = null;
  let contagem = { pendente: 0, aprovado: 0, rejeitado: 0 };

  raiz.innerHTML = `
    <div class="chips" id="filtros"></div>
    <div id="lista"><p class="carregando">Carregando…</p></div>`;

  const filtros = raiz.querySelector<HTMLElement>('#filtros')!;
  const lista = raiz.querySelector<HTMLElement>('#lista')!;

  function desenharFiltros() {
    const opcoes: [StatusAprovacao, string, number][] = [
      ['pendente', 'Pendentes', contagem.pendente],
      ['aprovado', 'Aprovadas', contagem.aprovado],
      ['rejeitado', 'Rejeitadas', contagem.rejeitado],
    ];
    filtros.innerHTML = opcoes
      .map(
        ([v, t, n]) =>
          `<button class="chip" data-f="${v}" aria-pressed="${filtro === v}">
             ${t}${n ? ` <span class="conta">${n}</span>` : ''}
           </button>`,
      )
      .join('');
  }

  filtros.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>('[data-f]');
    if (!b) return;
    filtro = b.dataset['f'] as StatusAprovacao;
    desenharFiltros();
    void carregar();
  });

  async function carregar() {
    aberta = null;
    try {
      const [r, c] = await Promise.all([
        api.get<{ total: number; aprovacoes: Aprovacao[] }>(
          `/api/aprovacoes?status=${filtro}&porPagina=50`,
        ),
        api.get<typeof contagem>('/api/aprovacoes/contagem'),
      ]);
      contagem = c;
      desenharFiltros();
      desenhar(r.aprovacoes);
    } catch (erro) {
      lista.innerHTML = `<p class="aviso">${escapar(
        erro instanceof Error ? erro.message : 'Não consegui carregar a fila.',
      )}</p>`;
    }
  }

  function desenhar(as: Aprovacao[]) {
    if (as.length === 0) {
      lista.innerHTML = `<div class="vazio"><p>${
        filtro === 'pendente'
          ? 'Nenhuma entrada esperando conferência. A fila está limpa.'
          : `Nada em “${filtro === 'aprovado' ? 'aprovadas' : 'rejeitadas'}”.`
      }</p></div>`;
      return;
    }
    lista.innerHTML = `<div class="fila">${as.map(cartao).join('')}</div>`;
    lista.querySelectorAll<HTMLElement>('[data-abrir]').forEach((el) => {
      el.addEventListener('click', () => void abrir(Number(el.dataset['abrir']), as));
    });
  }

  function cartao(a: Aprovacao): string {
    const p = a.palpite ?? {};
    const titulo = p.fornecedor || p.palpite_nome || a.tipo || 'Entrada';
    const detalhe = p.numero_nf
      ? `NF ${p.numero_nf}`
      : p.quantidade_estimada
        ? `${numero(p.quantidade_estimada)} ${p.unidade_provavel ?? ''}`.trim()
        : '';
    const quantos = p.itens?.length;

    return `
      <button class="fila-item" data-abrir="${a.id}">
        ${
          // A miniatura embutida vem primeiro: aparece com o JSON, não tem
          // como falhar. A busca por URL é só para foto sem miniatura gerada.
          a.miniatura
            ? `<img class="fila-foto" src="${a.miniatura}" alt="">`
            : a.foto_min
              ? `<img class="fila-foto" src="${escapar(api.foto(a.foto_min) ?? '')}" alt="" loading="lazy"
                    onerror="this.replaceWith(Object.assign(document.createElement('span'),{className:'fila-foto sem',textContent:'sem foto'}))">`
              : `<span class="fila-foto sem">sem foto</span>`
        }
        <span class="fila-corpo">
          <span class="fila-tipo">${escapar(a.tipo ?? '')}</span>
          <b class="fila-titulo">${escapar(titulo)}</b>
          <span class="fila-meta">
            ${detalhe ? escapar(detalhe) + ' · ' : ''}${
              quantos ? `${quantos} item(ns) na nota · ` : ''
            }${quando(a.enviada_em)}
          </span>
          ${a.operador_nome ? `<span class="fila-quem">enviou: ${escapar(a.operador_nome)}</span>` : ''}
          ${
            a.status === 'aprovado'
              ? `<span class="fila-selo ok">${a.itensLancados} entrada(s) lançada(s)</span>`
              : a.status === 'rejeitado'
                ? `<span class="fila-selo nao">rejeitada</span>`
                : ''
          }
        </span>
      </button>`;
  }

  // ------------------------------------------------------------- conferência

  async function abrir(id: number, todas: Aprovacao[]) {
    const a = todas.find((x) => x.id === id);
    if (!a) return;
    aberta = id;

    const linhas = linhasDoPalpite(a);
    detalhe(a, linhas);

    // Já procura o item de cada linha com o texto lido da nota. É o trabalho
    // chato que a pessoa faria à mão; a ESCOLHA continua sendo dela.
    for (const [i, l] of linhas.entries()) {
      if (aberta !== id) return;
      void sugerir(i, l.lido);
    }
  }

  /**
   * Transforma o palpite nas linhas do formulário.
   *
   * Os dois tipos de foto dão formatos diferentes: nota fiscal vem com uma
   * lista de itens; material sem nota vem com um palpite só. Vira uma lista nos
   * dois casos, para o resto da tela não precisar saber a diferença.
   */
  function linhasDoPalpite(a: Aprovacao): Linha[] {
    const p = a.palpite ?? {};
    if (p.itens?.length) {
      return p.itens.map((i) => ({
        lido: i.descricao ?? '',
        quantidade: i.quantidade ?? 1,
        lote: i.lote ?? '',
        validade: ehData(i.validade) ? i.validade! : '',
        marca: '',
      }));
    }
    return [
      {
        lido: p.palpite_nome ?? '',
        quantidade: p.quantidade_estimada ?? 1,
        lote: '',
        validade: '',
        marca: p.marca && !/n[ãa]o identificada/i.test(p.marca) ? p.marca : '',
      },
    ];
  }

  function detalhe(a: Aprovacao, linhas: Linha[]) {
    const p = a.palpite ?? {};
    const decidida = a.status !== 'pendente';

    lista.innerHTML = `
      <button class="voltar" id="voltar">← Toda a fila</button>
      <div class="conferencia">
        <div class="conf-foto">
          ${
            // Na tela vai a versão de 600 px (a original de celular passa de
            // 5 MB); o clique abre a original na janela de ampliar, que busca
            // pelo canal autenticado — era o `<a target="_blank">` que
            // dependia do cookie e deixava "A foto não abriu" sem saída.
            a.foto_url
              ? `<img src="${escapar(api.foto(a.foto_min) ?? '')}" alt="Foto enviada"
                      data-grande="${escapar(api.foto(a.foto_url) ?? '')}"
                      title="Clique para ver em tamanho original"
                      onerror="this.replaceWith(Object.assign(document.createElement('p'),{className:'aviso',textContent:'A foto não abriu.'}))">
                 <p class="dica">Clique na foto para ver em tamanho original.</p>`
              : `<div class="vazio"><p>Esta entrada foi registrada sem foto.</p></div>`
          }
        </div>

        <div class="conf-form">
          <header class="ficha-topo">
            <div>
              <span class="rotulo">${escapar(a.tipo ?? 'Entrada')}</span>
              <h2>${escapar(p.fornecedor || p.palpite_nome || 'Conferir entrada')}</h2>
            </div>
            ${
              decidida
                ? `<span class="estado ${a.status === 'aprovado' ? 'e-entregue' : 'e-cancelado'}">${
                    a.status === 'aprovado' ? 'Aprovada' : 'Rejeitada'
                  }</span>`
                : ''
            }
          </header>

          <p class="ficha-explica">
            Enviada ${quando(a.enviada_em)}${
              a.operador_nome ? ` por ${escapar(a.operador_nome)}` : ''
            }${a.decidida_em ? ` · decidida ${dataHora(a.decidida_em)}` : ''}${
              a.decidida_por_nome ? ` por ${escapar(a.decidida_por_nome)}` : ''
            }.
          </p>

          ${a.obs ? `<p class="ficha-obs">${escapar(a.obs)}</p>` : ''}
          ${
            a.motivo_recusa
              ? `<p class="aviso">Motivo da recusa: ${escapar(a.motivo_recusa)}</p>`
              : ''
          }
          ${
            p.observacoes
              ? `<details class="palpite-obs"><summary>O que a IA observou na foto</summary>
                   <p>${escapar(p.observacoes)}</p></details>`
              : ''
          }

          ${
            decidida
              ? `<div class="vazio"><p>${
                  a.status === 'aprovado'
                    ? `Esta entrada já foi aprovada e gerou ${a.itensLancados} lançamento(s) no estoque.`
                    : 'Esta entrada foi rejeitada — nada entrou no estoque.'
                }</p></div>`
              : formulario(a, p, linhas)
          }
        </div>
      </div>`;

    lista.querySelector('#voltar')!.addEventListener('click', () => void carregar());
    if (!decidida) ligarFormulario(a, linhas);
  }

  function formulario(a: Aprovacao, p: PalpiteNf, linhas: Linha[]): string {
    return `
      <form id="conf" novalidate>
        <div class="conf-cabecalho">
          <label class="rotulado">
            <span>Fornecedor</span>
            <input name="fornecedor" value="${escapar(p.fornecedor ?? '')}" maxlength="200">
          </label>
          <label class="rotulado estreito">
            <span>Número da NF</span>
            <input name="numeroNf" value="${escapar(p.numero_nf ?? '')}" maxlength="60">
          </label>
        </div>

        <h3>Itens a lançar</h3>
        <p class="dica">
          Cada linha vira uma entrada no estoque. <b>Escolha o item cadastrado</b> —
          o texto da nota fica ao lado para conferir.
        </p>
        <div id="linhas">${linhas.map((l, i) => linhaHtml(a, l, i)).join('')}</div>

        <div class="conf-acoes">
          <button type="submit" class="btn" id="aprovar">Aprovar e lançar no estoque</button>
          <button type="button" class="btn btn-claro" id="rejeitar">Rejeitar…</button>
        </div>
        <p class="aviso" id="erro" hidden></p>
      </form>`;
  }

  function linhaHtml(a: Aprovacao, l: Linha, i: number): string {
    // Em material sem NF, a foto da identificação É a foto do produto — em
    // nota fiscal ela é a nota, e nota não é cara de item.
    const comFoto = a.tipo !== 'NF' && !!a.foto_url;
    return `
      <fieldset class="conf-linha" data-linha="${i}">
        <legend class="conf-lido">${escapar(l.lido || '(a IA não leu descrição)')}</legend>

        <div class="conf-item" data-alvo="item">
          ${escolhaHtml(l)}
        </div>

        <div class="conf-campos">
          <label class="rotulado estreito">
            <span>Quantidade</span>
            <input name="quantidade" type="number" step="any" min="0.001" value="${l.quantidade}">
          </label>
          <label class="rotulado estreito">
            <span>Lote</span>
            <input name="lote" value="${escapar(l.lote)}" maxlength="60">
          </label>
          <label class="rotulado estreito">
            <span>Validade</span>
            <input name="validade" type="date" value="${escapar(l.validade)}">
          </label>
          <label class="rotulado estreito">
            <span>Marca</span>
            <input name="marca" value="${escapar(l.marca)}" maxlength="120">
          </label>
        </div>

        ${comFoto ? `<div class="conf-fotos" data-alvo="fotos">${fotosHtml(a, l)}</div>` : ''}
      </fieldset>`;
  }

  /**
   * A foto da identificação virando foto do item, com a segunda e a troca de
   * capa — replicado do sistema antigo. Só aparece com um item escolhido: a
   * foto precisa de um dono.
   */
  function fotosHtml(a: Aprovacao, l: Linha): string {
    if (!l.escolhido) return '';
    const f = l.fotoItem;
    if (!f) return '';
    const capaEhSegunda = f.capa === 'segunda' && !!f.segunda;
    const slotAprovacao = `<img src="${escapar(api.foto(a.foto_min) ?? '')}" alt="Foto da identificação">`;
    const slotSegunda = f.segunda
      ? `<img src="${escapar(api.foto(f.segunda.url) ?? '')}" alt="Segunda foto">`
      : '';
    return `
      <label class="conf-fotos-usar">
        <input type="checkbox" data-foto-usar ${f.usar ? 'checked' : ''}>
        <span>Usar como foto de <b>${escapar(l.escolhido.codigo)}</b>${
          l.escolhido.miniatura || l.escolhido.foto ? ' (o item já tem foto — estas entram na frente)' : ' — o item está sem foto'
        }</span>
      </label>
      <div class="conf-fotos-slots" ${f.usar ? '' : 'hidden'}>
        <figure class="cf-slot">
          ${capaEhSegunda ? slotSegunda : slotAprovacao}
          <figcaption>capa</figcaption>
        </figure>
        ${
          f.segunda
            ? `<button type="button" class="cf-trocar" data-foto-trocar title="Trocar qual é a capa">⇄</button>
               <figure class="cf-slot">
                 ${capaEhSegunda ? slotAprovacao : slotSegunda}
                 <figcaption>segunda</figcaption>
               </figure>`
            : `<button type="button" class="cf-add" data-foto-add>+ segunda foto</button>
               <input type="file" accept="image/*" data-foto-arquivo hidden>`
        }
      </div>`;
  }

  /** O bloco que mostra o item escolhido, ou a busca quando ainda não há um. */
  function escolhaHtml(l: Linha): string {
    if (l.escolhido) {
      return `
        <div class="conf-escolhido">
          <span class="cod">${escapar(l.escolhido.codigo)}</span>
          <b>${escapar(l.escolhido.nome)}</b>
          <span class="sub-inline">saldo ${numero(l.escolhido.saldo)} ${escapar(
            l.escolhido.unidade ?? '',
          )}</span>
          <button type="button" class="btn btn-claro" data-trocar>Trocar</button>
        </div>`;
    }
    return `
      <div class="conf-busca">
        <label class="rotulado">
          <span>Item do estoque</span>
          <input data-busca placeholder="Procure pelo nome ou código…" autocomplete="off">
        </label>
        <div class="conf-sugestoes" data-sugestoes></div>
      </div>`;
  }

  // ------------------------------------------------------- busca de item

  /**
   * Procura o item de uma linha e mostra os candidatos.
   *
   * Não escolhe sozinho nem quando há um resultado só. A busca por semelhança
   * SEMPRE devolve algo quando algo se parece um pouco — e "um pouco parecido"
   * é exatamente como material errado entra no estoque sem ninguém notar.
   */
  async function sugerir(i: number, termo: string): Promise<void> {
    const bloco = lista.querySelector<HTMLElement>(`[data-linha="${i}"] [data-sugestoes]`);
    if (!bloco || !termo.trim()) return;
    bloco.innerHTML = `<p class="carregando">Procurando…</p>`;
    try {
      const r = await api.get<{ itens: ItemDaLista[]; porSemelhanca: boolean }>(
        `/api/itens?busca=${encodeURIComponent(termo)}&porPagina=6`,
      );
      if (r.itens.length === 0) {
        bloco.innerHTML = `<p class="dica">Nada parecido no cadastro. Ajuste a busca — ou cadastre o item antes de aprovar.</p>`;
        return;
      }
      bloco.innerHTML = `
        ${r.porSemelhanca ? `<p class="dica">Nada exato; estes são os mais parecidos.</p>` : ''}
        <ul class="sugestoes">
          ${r.itens
            .map(
              (it) => `<li><button type="button" class="sugestao" data-cod="${escapar(it.codigo)}">
                  <span class="cod">${escapar(it.codigo)}</span>
                  <span class="sug-nome">${escapar(it.nome)}</span>
                  <span class="sub-inline">${numero(it.saldo)} ${escapar(it.unidade ?? '')}</span>
                </button></li>`,
            )
            .join('')}
        </ul>`;
      // Guarda os objetos para não ter que buscar de novo ao clicar.
      candidatos.set(i, r.itens);
    } catch {
      bloco.innerHTML = `<p class="aviso">Não consegui procurar agora.</p>`;
    }
  }

  const candidatos = new Map<number, ItemDaLista[]>();

  // ------------------------------------------------------------ interação

  function ligarFormulario(a: Aprovacao, linhas: Linha[]) {
    const form = lista.querySelector<HTMLFormElement>('#conf')!;
    const erro = lista.querySelector<HTMLElement>('#erro')!;

    /** Redesenha só o bloco do item, preservando o resto que já foi digitado. */
    function redesenharItem(i: number) {
      const alvo = lista.querySelector<HTMLElement>(`[data-linha="${i}"] [data-alvo="item"]`);
      if (!alvo) return;
      alvo.innerHTML = escolhaHtml(linhas[i]!);
      redesenharFotos(i);
    }

    function redesenharFotos(i: number) {
      const alvo = lista.querySelector<HTMLElement>(`[data-linha="${i}"] [data-alvo="fotos"]`);
      if (alvo) alvo.innerHTML = fotosHtml(a, linhas[i]!);
    }

    // Digitar na busca: espera a pessoa parar antes de consultar. Sem isso, uma
    // consulta por tecla — e a resposta de uma busca antiga pode chegar depois
    // da nova e sobrescrever o resultado certo.
    let temporizador: number | undefined;
    form.addEventListener('input', (e) => {
      const alvo = e.target as HTMLElement;
      if (!alvo.hasAttribute('data-busca')) return;
      const i = Number(alvo.closest<HTMLElement>('[data-linha]')!.dataset['linha']);
      window.clearTimeout(temporizador);
      temporizador = window.setTimeout(
        () => void sugerir(i, (alvo as HTMLInputElement).value),
        300,
      );
    });

    // Enter na busca não pode enviar o formulário: a pessoa ainda está
    // escolhendo o item, e enviar aqui aprovaria a entrada pela metade.
    form.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && (e.target as HTMLElement).hasAttribute('data-busca')) {
        e.preventDefault();
      }
    });

    form.addEventListener('click', (e) => {
      const alvo = e.target as HTMLElement;

      const sug = alvo.closest<HTMLElement>('.sugestao');
      if (sug) {
        const i = Number(sug.closest<HTMLElement>('[data-linha]')!.dataset['linha']);
        const it = candidatos.get(i)?.find((x) => x.codigo === sug.dataset['cod']);
        if (it) {
          const l = linhas[i]!;
          l.escolhido = it;
          // Item sem foto já vem com "usar" marcado — é o caso em que a foto
          // da identificação mais vale; com foto, a pessoa decide.
          if (a.tipo !== 'NF' && a.foto_url) {
            l.fotoItem = {
              usar: !it.miniatura && !it.foto,
              capa: 'aprovacao',
              ...(l.fotoItem?.segunda ? { segunda: l.fotoItem.segunda } : {}),
            };
          }
          redesenharItem(i);
        }
        return;
      }

      if (alvo.closest('[data-trocar]')) {
        const i = Number(alvo.closest<HTMLElement>('[data-linha]')!.dataset['linha']);
        delete linhas[i]!.escolhido;
        redesenharItem(i);
        void sugerir(i, linhas[i]!.lido);
        return;
      }

      if (alvo.closest('[data-foto-add]')) {
        alvo
          .closest<HTMLElement>('[data-alvo="fotos"]')!
          .querySelector<HTMLInputElement>('[data-foto-arquivo]')!
          .click();
        return;
      }

      if (alvo.closest('[data-foto-trocar]')) {
        const i = Number(alvo.closest<HTMLElement>('[data-linha]')!.dataset['linha']);
        const f = linhas[i]!.fotoItem;
        if (f) {
          f.capa = f.capa === 'segunda' ? 'aprovacao' : 'segunda';
          redesenharFotos(i);
        }
      }
    });

    form.addEventListener('change', (e) => {
      const alvo = e.target as HTMLInputElement;
      const linhaEl = alvo.closest<HTMLElement>('[data-linha]');
      if (!linhaEl) return;
      const i = Number(linhaEl.dataset['linha']);
      const f = linhas[i]?.fotoItem;

      if (alvo.hasAttribute('data-foto-usar') && f) {
        f.usar = alvo.checked;
        redesenharFotos(i);
        return;
      }

      if (alvo.hasAttribute('data-foto-arquivo') && f && alvo.files?.[0]) {
        const arquivo = alvo.files[0];
        void api
          .enviarFoto('itens', arquivo, linhas[i]?.escolhido?.codigo)
          .then((r) => {
            f.segunda = { caminho: r.caminho, url: r.url };
            redesenharFotos(i);
          })
          .catch(() => {
            avisarBloqueante('Não consegui subir a segunda foto. Tente de novo.');
          });
      }
    });

    lista.querySelector('#rejeitar')!.addEventListener('click', () => void rejeitar(a));

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      void aprovar(a, linhas, form, erro);
    });
  }

  async function aprovar(
    a: Aprovacao,
    linhas: Linha[],
    form: HTMLFormElement,
    erro: HTMLElement,
  ): Promise<void> {
    // Lê os campos digitados de volta para as linhas: entre desenhar e enviar,
    // a pessoa mexeu em quantidade, lote, validade e marca.
    lista.querySelectorAll<HTMLElement>('[data-linha]').forEach((el) => {
      const i = Number(el.dataset['linha']);
      const l = linhas[i];
      if (!l) return;
      const v = (n: string) => el.querySelector<HTMLInputElement>(`[name="${n}"]`)?.value ?? '';
      l.quantidade = Number(v('quantidade'));
      l.lote = v('lote').trim();
      l.validade = v('validade');
      l.marca = v('marca').trim();
    });

    const semItem = linhas
      .map((l, i) => (l.escolhido ? null : i + 1))
      .filter((n): n is number => n !== null);
    if (semItem.length) {
      mostrarErro(
        erro,
        `Escolha o item do estoque ${
          semItem.length === 1 ? `da linha ${semItem[0]}` : `das linhas ${semItem.join(', ')}`
        }. Sem isso a entrada não tem onde ser lançada.`,
      );
      return;
    }
    const semQtd = linhas.filter((l) => !(l.quantidade > 0));
    if (semQtd.length) {
      mostrarErro(erro, 'Toda linha precisa de uma quantidade maior que zero.');
      return;
    }

    const dados = new FormData(form);
    const botao = form.querySelector<HTMLButtonElement>('#aprovar')!;
    botao.disabled = true;
    botao.textContent = 'Lançando…';

    try {
      const r = await api.post<{ itensLancados: number }>(`/api/aprovacoes/${a.id}/aprovacao`, {
        fornecedor: String(dados.get('fornecedor') ?? '').trim() || undefined,
        numeroNf: String(dados.get('numeroNf') ?? '').trim() || undefined,
        itens: linhas.map((l) => ({
          codigo: l.escolhido!.codigo,
          quantidade: l.quantidade,
          ...(l.lote ? { lote: l.lote } : {}),
          ...(l.validade ? { validade: l.validade } : {}),
          ...(l.marca ? { marca: l.marca } : {}),
          ...(l.fotoItem?.usar
            ? {
                fotoParaItem: {
                  capa: l.fotoItem.capa,
                  ...(l.fotoItem.segunda ? { segunda: l.fotoItem.segunda.caminho } : {}),
                },
              }
            : {}),
        })),
      });
      lista.innerHTML = `
        <div class="vazio">
          <p><b>Aprovada.</b> ${r.itensLancados} entrada(s) lançada(s) no estoque.</p>
          <button class="btn" id="voltar-fila">Voltar à fila</button>
        </div>`;
      lista.querySelector('#voltar-fila')!.addEventListener('click', () => void carregar());
    } catch (e) {
      botao.disabled = false;
      botao.textContent = 'Aprovar e lançar no estoque';
      const msg =
        e instanceof ErroApi
          ? e.message + (e.detalhes?.length ? ` (${e.detalhes.join(', ')})` : '')
          : 'Não consegui lançar.';
      mostrarErro(erro, msg);
    }
  }

  async function rejeitar(a: Aprovacao): Promise<void> {
    const motivo = prompt(
      'Por que esta entrada está sendo rejeitada?\n\nO motivo fica registrado — é o que explica, depois, por que o material não entrou.',
    );
    if (motivo === null) return;
    if (motivo.trim().length < 3) {
      avisarBloqueante('Escreva um motivo — nem que seja curto.');
      return;
    }
    try {
      await api.post(`/api/aprovacoes/${a.id}/rejeicao`, { motivo: motivo.trim() });
      await carregar();
    } catch (e) {
      avisarBloqueante(e instanceof ErroApi ? e.message : 'Não consegui rejeitar.');
    }
  }

  function mostrarErro(el: HTMLElement, msg: string) {
    el.textContent = msg;
    el.hidden = false;
    el.scrollIntoView({ block: 'nearest' });
  }

  desenharFiltros();
  void carregar();
}

/** A IA às vezes devolve "" ou "Não visível" onde deveria vir uma data. */
function ehData(v: string | undefined): boolean {
  return !!v && /^\d{4}-\d{2}-\d{2}$/.test(v);
}
